import type { AxiosResponse } from 'axios'
import { apiDjango } from './ApiDjango'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import {
  ImportApiResult,
  ImportCreateResponseDTO,
  ImportFileUrlDTO,
  ImportHeaderDTO,
  ImportInsertRequestDTO,
  ImportListPageDTO,
  ImportListParams,
  ImportProgressDTO,
  ImportUpdateRequestDTO,
} from '../interfaces/Import/ShipmentBulkUploadApi'

const { REACT_APP_URL_PATH_API_DJANGO } = process.env
// Base del endpoint asíncrono de importación masiva (el backend la renombró a bulk-upload).
const IMPORT_BASE = `${REACT_APP_URL_PATH_API_DJANGO}/shipment/bulk-upload`

export async function insertShipmentList(payload: {
  shipment_list: any[]
}): Promise<ListResponse<any>> {
  try {
    const url = `${REACT_APP_URL_PATH_API_DJANGO}/shipment`
    const resp = await apiDjango.post(url, payload)
    if (
      resp.status === 200 &&
      resp.data &&
      (resp.data as any).error === false
    ) {
      return {
        didError: false,
        message: (resp.data as any).message ?? 'OK',
        errorMessage: '',
        model: (resp.data as any).data,
      }
    }
    const d = resp.data as any
    const msg =
      d?.message ||
      d?.detail ||
      JSON.stringify(d?.errors) ||
      'Error creando guías'
    return { didError: true, message: msg, errorMessage: msg, model: null }
  } catch (error: any) {
    const msg = error?.message || 'Error llamando al API de guías'
    return { didError: true, message: msg, errorMessage: msg, model: null }
  }
}

// ---------------------------------------------------------------------------
// Importación asíncrona de guías — /api/shipment/bulk-upload/*
// ---------------------------------------------------------------------------

/**
 * Desenvuelve el envelope estándar { code, error, message, data, error_data }.
 * REGLA (spec §6.2): NUNCA ramificar por status HTTP; siempre mirar `error`/`code` del body.
 * El backend responde 200 incluso en errores de negocio; el catch cubre red caída y, de
 * forma defensiva, un 4xx/5xx con envelope en e.response.data.
 */
async function unwrap<T>(
  p: Promise<AxiosResponse<any>>
): Promise<ImportApiResult<T>> {
  try {
    const resp = await p
    const body = (resp?.data ?? {}) as any
    const isError = body.error === true
    return {
      didError: isError,
      code: body.code ?? (isError ? 'unknown' : '0'),
      message: body.message ?? (isError ? 'Error' : 'OK'),
      errorMessage: isError ? (body.message ?? 'Error') : '',
      model: (body.data ?? null) as T | null,
      errorData: body.error_data ?? {},
    }
  } catch (e: any) {
    const body = e?.response?.data
    if (body && typeof body === 'object' && 'error' in body) {
      return {
        didError: true,
        code: body.code ?? 'unknown',
        message: body.message ?? 'Error',
        errorMessage: body.message ?? 'Error',
        model: null,
        errorData: body.error_data ?? {},
      }
    }
    const msg = e?.message || 'Error de red'
    return {
      didError: true,
      code: 'network',
      message: msg,
      errorMessage: msg,
      model: null,
      errorData: {},
    }
  }
}

/**
 * POST /shipment/bulk-upload/ — crea una importación.
 * Con archivo → multipart/form-data (payload = JSON.stringify(dto), file). NO setear
 * Content-Type: el browser arma el boundary (spec §6.4). Sin archivo → JSON directo.
 */
export function createImport(args: {
  dto: ImportInsertRequestDTO
  file?: File | null
}): Promise<ImportApiResult<ImportCreateResponseDTO>> {
  const { dto, file } = args
  const url = `${IMPORT_BASE}`
  if (file) {
    const form = new FormData()
    form.append('payload', JSON.stringify(dto))
    form.append('file', file)
    return unwrap<ImportCreateResponseDTO>(apiDjango.post(url, form))
  }
  return unwrap<ImportCreateResponseDTO>(apiDjango.post(url, dto))
}

/** GET /shipment/bulk-upload/list — listado paginado con filtros. */
export function getImportList(
  params: ImportListParams = {}
): Promise<ImportApiResult<ImportListPageDTO>> {
  return unwrap<ImportListPageDTO>(
    apiDjango.get(`${IMPORT_BASE}/list`, { params })
  )
}

/** GET /shipment/bulk-upload/<id> — detalle completo (header + details + logs). */
export function getImport(
  headerId: string
): Promise<ImportApiResult<ImportHeaderDTO>> {
  return unwrap<ImportHeaderDTO>(apiDjango.get(`${IMPORT_BASE}/${headerId}`))
}

/** GET /shipment/bulk-upload/<id>/progress — endpoint del polling (liviano). */
export function getImportProgress(
  headerId: string
): Promise<ImportApiResult<ImportProgressDTO>> {
  return unwrap<ImportProgressDTO>(
    apiDjango.get(`${IMPORT_BASE}/${headerId}/progress`)
  )
}

/** GET /shipment/bulk-upload/<id>/file — URL firmada (expira 15 min). */
export function getImportFile(
  headerId: string
): Promise<ImportApiResult<ImportFileUrlDTO>> {
  return unwrap<ImportFileUrlDTO>(
    apiDjango.get(`${IMPORT_BASE}/${headerId}/file`)
  )
}

/**
 * PUT /shipment/bulk-upload/<id>/update — corrige líneas en error in-place.
 * Body: { corrections: [{ shipment_bulk_upload_detail_id, shipment }] }. Cada corrección
 * pasa el detalle de 108 (Error) → 106 (Pending), limpia el error y lo reprocesa.
 * NO crea líneas nuevas (eso lo hacía el viejo body { shipment_list }).
 */
export function updateImport(
  headerId: string,
  body: ImportUpdateRequestDTO
): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.put(`${IMPORT_BASE}/${headerId}/update`, body))
}

/** POST /shipment/bulk-upload/<id>/execute — reintento manual (retry_errors_only por defecto). */
export function executeImport(
  headerId: string,
  retryErrorsOnly = true
): Promise<ImportApiResult<any>> {
  return unwrap<any>(
    apiDjango.post(`${IMPORT_BASE}/${headerId}/execute`, {
      retry_errors_only: retryErrorsOnly,
    })
  )
}

/** POST /shipment/bulk-upload/<id>/cancel — cancela (solo si InProcess). */
export function cancelImport(headerId: string): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.post(`${IMPORT_BASE}/${headerId}/cancel`, {}))
}

/** DELETE /shipment/bulk-upload/<id>/delete — soft-delete (solo InProcess y 0 Executed). */
export function deleteImport(headerId: string): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.delete(`${IMPORT_BASE}/${headerId}/delete`))
}

/** DELETE /shipment/bulk-upload/<id>/details/<detailId> — borra un detalle (si no Executed). */
export function deleteImportDetail(
  headerId: string,
  detailId: string
): Promise<ImportApiResult<any>> {
  return unwrap<any>(
    apiDjango.delete(`${IMPORT_BASE}/${headerId}/details/${detailId}`)
  )
}
